import { create } from 'zustand';
import { persist } from 'zustand/middleware';

const API_URL = process.env.REACT_APP_BACKEND_URL || (typeof window !== 'undefined' ? ((window.location.hostname === 'devapp.soviewfx.fr' || window.location.hostname === 'devadmin.soviewfx.fr') ? 'https://devhub.soviewfx.fr' : ((window.location.hostname === 'app.soviewfx.fr' || window.location.hostname === 'admin.soviewfx.fr') ? 'https://hub.soviewfx.fr' : '')) : '');

// Auth Store
export const useAuthStore = create(
  persist(
    (set, get) => ({
      user: null,
      token: null,
      isLoading: false,
      
      setUser: (user) => set({ user }),
      setToken: (token) => set({ token }),
      
      login: async (email, password) => {
        set({ isLoading: true });
        try {
          const res = await fetch(`${API_URL}/api/auth/login`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            credentials: 'include',
            body: JSON.stringify({ email, password })
          });
          if (!res.ok) throw new Error('Invalid credentials');
          const data = await res.json();
          set({ user: data.user, token: data.token, isLoading: false });
          return data;
        } catch (error) {
          set({ isLoading: false });
          throw error;
        }
      },
      
      register: async (email, password, name, lang = 'fr') => {
        set({ isLoading: true });
        try {
          const res = await fetch(`${API_URL}/api/auth/register`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ email, password, name, lang })
          });
          if (!res.ok) {
            const err = await res.json();
            throw new Error(err.detail || 'Registration failed');
          }
          const data = await res.json();
          set({ user: data.user, token: data.token, isLoading: false });
          return data;
        } catch (error) {
          set({ isLoading: false });
          throw error;
        }
      },
      
      checkAuth: async () => {
        // Skip if returning from OAuth
        if (window.location.hash?.includes('session_id=')) {
          return null;
        }
        
        try {
          const res = await fetch(`${API_URL}/api/auth/me`, {
            credentials: 'include',
            headers: get().token ? { Authorization: `Bearer ${get().token}` } : {}
          });
          if (!res.ok) {
            set({ user: null, token: null });
            return null;
          }
          const user = await res.json();
          set({ user });
          return user;
        } catch {
          set({ user: null, token: null });
          return null;
        }
      },
      
      logout: async () => {
        try {
          await fetch(`${API_URL}/api/auth/logout`, {
            method: 'POST',
            credentials: 'include'
          });
        } catch {}
        set({ user: null, token: null });
      },
      
      updateCredits: (credits) => {
        const user = get().user;
        if (user) {
          set({ user: { ...user, credits } });
        }
      }
    }),
    {
      name: 'soview-auth',
      partialize: (state) => ({ token: state.token })
    }
  )
);

// App Store (configurator state)
export const useAppStore = create((set, get) => ({
  // Language
  lang: localStorage.getItem('soview_lang') || 'fr',
  setLang: (lang) => {
    localStorage.setItem('soview_lang', lang);
    set({ lang });
  },
  
  // Current object type
  currentObject: null,
  setCurrentObject: (obj) => set({ currentObject: obj }),
  
  // Photo state
  photo: null,
  photoBase64: null,
  setPhoto: (photo, base64) => set({
    photo,
    photoBase64: base64,
    currentObject: null,
    iaImage: null,
    iaStack: [],
    iaIndex: -1,
    measure: { active: false, taps: [], pixDist: 0, cmVal: 0, scale: 0, hasMeasure: false },
  }),
  
  // Measure state
  measure: {
    active: false,
    taps: [],
    pixDist: 0,
    cmVal: 0,
    scale: 0,
    hasMeasure: false
  },
  setMeasure: (measure) => set({ measure: { ...get().measure, ...measure } }),
  resetMeasure: () => set({ 
    measure: { active: false, taps: [], pixDist: 0, cmVal: 0, scale: 0, hasMeasure: false }
  }),
  
  // AI state
  iaImage: null,
  iaLoading: false,
  iaStack: [],
  iaIndex: -1,
  setIaImage: (url) => {
    const stack = [...get().iaStack, url];
    set({ iaImage: url, iaStack: stack, iaIndex: stack.length - 1 });
  },
  setIaLoading: (loading) => set({ iaLoading: loading }),
  undoIa: () => {
    const { iaStack, iaIndex } = get();
    if (iaIndex > 0) {
      const newIndex = iaIndex - 1;
      set({ iaIndex: newIndex, iaImage: iaStack[newIndex] });
    }
  },
  redoIa: () => {
    const { iaStack, iaIndex } = get();
    if (iaIndex < iaStack.length - 1) {
      const newIndex = iaIndex + 1;
      set({ iaIndex: newIndex, iaImage: iaStack[newIndex] });
    }
  },
  resetIa: () => set({ iaImage: null, iaStack: [], iaIndex: -1 }),
  
  // Weather mode (day/night)
  weatherMode: 'day',
  toggleWeather: () => set({ weatherMode: get().weatherMode === 'day' ? 'night' : 'day' }),
  
  // Object configs
  configs: {
    store: {
      width: 400,
      height: 350,
      depth: 300,
      toile_color: 'Écru',
      struct_color: '7016',
      led: false
    },
    pergola: {
      width: 400,
      height: 280,
      depth: 350,
      struct_color: 'Anthracite',
      roof_color: 'Anthracite',
      feet: 4,
      screens: [false, false, false]
    },

  },
  updateConfig: (objType, updates) => {
    const configs = { ...get().configs };
    configs[objType] = { ...configs[objType], ...updates };
    set({ configs });
  },
  
  // Reset
  reset: () => set({
    currentObject: null,
    photo: null,
    photoBase64: null,
    iaImage: null,
    iaStack: [],
    iaIndex: -1,
    measure: { active: false, taps: [], pixDist: 0, cmVal: 0, hasMeasure: false }
  })
}));

// Projects Store
export const useProjectsStore = create((set, get) => ({
  projects: [],
  loading: false,
  
  fetchProjects: async () => {
    const { token } = useAuthStore.getState();
    if (!token) return;
    
    set({ loading: true });
    try {
      const res = await fetch(`${API_URL}/api/projects`, {
        credentials: 'include',
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        const projects = await res.json();
        set({ projects, loading: false });
      }
    } catch {
      set({ loading: false });
    }
  },
  
  saveProject: async (projectData) => {
    const { token } = useAuthStore.getState();
    if (!token) throw new Error('Not authenticated');
    
    const res = await fetch(`${API_URL}/api/projects`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`
      },
      credentials: 'include',
      body: JSON.stringify(projectData)
    });
    
    if (!res.ok) throw new Error('Failed to save project');
    const project = await res.json();
    set({ projects: [...get().projects, project] });
    return project;
  },
  
  deleteProject: async (projectId) => {
    const { token } = useAuthStore.getState();
    if (!token) return;
    
    const res = await fetch(`${API_URL}/api/projects/${projectId}`, {
      method: 'DELETE',
      credentials: 'include',
      headers: { Authorization: `Bearer ${token}` }
    });
    
    if (res.ok) {
      set({ projects: get().projects.filter(p => p.project_id !== projectId) });
    }
  }
}));
